import { ColumnInfo } from '../../utils/dataAnalyzer';
import { DatasetProfile } from './types';
import { parseNumericColumn, skewness, mean, stdDev } from './statistics';

const isValidDate = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') return false;
  const parsed = new Date(String(value));
  return Number.isFinite(parsed.getTime());
};

const getDuplicateRows = (rows: any[]): number => {
  const seen = new Set<string>();
  let duplicates = 0;

  rows.forEach((row) => {
    const serialized = JSON.stringify(row);
    if (seen.has(serialized)) {
      duplicates += 1;
      return;
    }
    seen.add(serialized);
  });

  return duplicates;
};

export const buildDatasetProfile = (
  rows: any[],
  headers: string[],
  columnInfo: Map<string, ColumnInfo>
): DatasetProfile => {
  const duplicateRows = getDuplicateRows(rows);
  const dateColumns = Array.from(columnInfo.values()).filter((column) => column.type === 'datetime');

  const profile: DatasetProfile = {
    rowCount: rows.length,
    columnCount: headers.length,
    duplicateRows,
    duplicateRatio: rows.length ? duplicateRows / rows.length : 0,
    columns: headers.map((header) => {
      const info = columnInfo.get(header);
      const values = rows.map((row) => row[header]);
      const nonNullValues = values.filter((value) => value !== null && value !== undefined && value !== '');
      const nullCount = values.length - nonNullValues.length;
      const type = info?.type ?? 'categorical';

      const columnProfile = {
        name: header,
        type,
        nullCount,
        nullRatio: rows.length ? nullCount / rows.length : 0,
        uniqueCount: new Set(nonNullValues).size,
        invalidDateCount: type === 'datetime'
          ? nonNullValues.filter((value) => !isValidDate(value)).length
          : undefined
      };

      if (type === 'numeric') {
        const numericValues = parseNumericColumn(rows, header);
        return {
          ...columnProfile,
          min: numericValues.length ? Math.min(...numericValues) : undefined,
          max: numericValues.length ? Math.max(...numericValues) : undefined,
          mean: numericValues.length ? mean(numericValues) : undefined,
          stdDev: numericValues.length ? stdDev(numericValues) : undefined,
          skewness: numericValues.length >= 3 ? skewness(numericValues) : undefined
        };
      }

      return columnProfile;
    })
  };

  if (dateColumns.length > 0) {
    const dateValues = headers
      .filter((header) => columnInfo.get(header)?.type === 'datetime')
      .flatMap((header) => rows.map((row) => new Date(row[header]).getTime()))
      .filter((timestamp) => Number.isFinite(timestamp))
      .sort((a, b) => a - b);

    if (dateValues.length > 0) {
      profile.dateCoverage = {
        start: new Date(dateValues[0]).toISOString(),
        end: new Date(dateValues[dateValues.length - 1]).toISOString()
      };
    }
  }

  return profile;
};
